
import { useState, type ComponentProps, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";

interface CardProps extends ComponentProps<"div"> {
  /** Adds the flyout shadow on hover: clickable tiles and rows. */
  interactive?: boolean;
  tone?: "default" | "dark" | "dashed" | "tinted";
  padding?: "none" | "sm" | "md" | "lg";
}

const pads = { none: "", sm: "p-4", md: "p-[22px]", lg: "px-10 py-9" };
const toneClass = {
  default: "bg-white border border-line",
  dark: "bg-[linear-gradient(135deg,#1B1C31,#2A2B45)] text-white",
  dashed: "border border-dashed border-line-strong",
  tinted: "bg-primary-50",
};

export function Card({ interactive, tone = "default", padding = "md", className, ...rest }: CardProps) {
  return (
    <div
      className={cn(
        "rounded-2xl",
        toneClass[tone],
        pads[padding],
        interactive && "transition-shadow hover:shadow-hover",
        className,
      )}
      {...rest}
    />
  );
}

export function MicroCap({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("text-[11px] font-bold uppercase tracking-[0.07em] text-muted", className)}>{children}</div>;
}

/** Purple uppercase kicker above page titles ("TABLEAU DE BORD"). */
export function Kicker({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("text-[11px] font-bold uppercase tracking-[0.09em] text-primary-500", className)}>{children}</div>;
}

export function StatCard({ label, value, delta, deltaTone = "success", className }: { label: string; value: ReactNode; delta?: ReactNode; deltaTone?: "success" | "danger" | "muted"; className?: string }) {
  return (
    <Card padding="none" className={cn("p-5", className)}>
      <MicroCap className="tracking-[0.06em]">{label}</MicroCap>
      <div className="mt-2.5 text-2xl font-extrabold text-ink">{value}</div>
      {delta && (
        <div className={cn("mt-1.5 text-xs font-semibold", { success: "text-success", danger: "text-danger", muted: "text-muted" }[deltaTone])}>{delta}</div>
      )}
    </Card>
  );
}

interface SectionCardProps {
  title: ReactNode;
  badge?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}

export function SectionCard({ title, badge, defaultOpen = true, className, children }: SectionCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card padding="none" className={className}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex w-full items-center justify-between px-5 py-4 text-left">
        <span className="flex items-center gap-2.5 text-[13px] font-bold text-ink">
          {title}
          {badge}
        </span>
        <ChevronDown className={cn("size-3.5 text-muted transition-transform", open && "rotate-180")} strokeWidth={2.4} />
      </button>
      {open && <div className="border-t border-line px-5 py-3.5">{children}</div>}
    </Card>
  );
}
